import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTable, useSortBy } from 'react-table';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Input, Button, Flex, Link, Text,
  Select, Badge, Spinner
} from "@chakra-ui/react";
import { AppContext } from '../components/AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

function ExternalCaseTable() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState([{ id: 'date_created', desc: true }]);
  const [caseTypeFilter, setCaseTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  const { accessToken } = useContext(AppContext);
  const rowsPerPage = 25;

  const fetchCases = async (page = 1, search = '', caseType = '', status = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: rowsPerPage.toString(),
        sort_by: sortBy[0]?.id || 'date_created',
        sort_order: sortBy[0]?.desc ? 'desc' : 'asc',
        ...(search && { search }),
        ...(caseType && { case_type: caseType }),
        ...(status && { status })
      });

      const response = await callApi(`/external-cases?${params}`, accessToken);
      const data = await response.json();
      
      if (data.status === 'success') {
        setCases(data.data);
        setTotalPages(data.pagination.pages);
        setTotalCount(data.pagination.total);
      } else {
        console.error('API Error:', data.message);
        setCases([]);
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
      setCases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchCases(currentPage, searchTerm, caseTypeFilter, statusFilter);
    }
  }, [accessToken, currentPage, sortBy, caseTypeFilter, statusFilter]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchCases(1, searchTerm, caseTypeFilter, statusFilter);
  };

  const handleSort = (column) => {
    const isDesc = sortBy[0]?.id === column.id && !sortBy[0]?.desc;
    setSortBy([{ id: column.id, desc: isDesc }]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getStatusBadge = (status) => {
    const colorMap = {
      'completed': 'green',
      'in_progress': 'blue',
      'pending': 'yellow',
      'failed': 'red'
    };
    return (
      <Badge colorScheme={colorMap[status] || 'gray'}>
        {status || 'Unknown'}
      </Badge>
    );
  };

  const columns = useMemo(() => [
    {
      Header: 'Case ID',
      accessor: 'external_case_id',
      Cell: ({ value, row }) => (
        <Link 
          color="blue.500" 
          onClick={() => window.open(`/case-details/${value}`, '_blank')}
          cursor="pointer"
        >
          {value}
        </Link>
      )
    },
    {
      Header: 'Patient ID',
      accessor: 'external_patient_id',
      Cell: ({ value }) => value || 'N/A'
    },
    {
      Header: 'Case Description',
      accessor: 'case_description',
      Cell: ({ value }) => (
        <Text title={value}>
          {truncateText(value, 80)}
        </Text>
      )
    },
    {
      Header: 'Case Type',
      accessor: 'case_type'
    },
    {
      Header: 'Date Created',
      accessor: 'date_created',
      Cell: ({ value }) => formatDate(value)
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => getStatusBadge(value)
    },
    {
      Header: 'Carrier',
      accessor: 'carrier_name',
      Cell: ({ value, row }) => (
        <Box>
          <Text fontSize="sm">{value || 'N/A'}</Text>
          {row.original.payer_type && (
            <Text fontSize="xs" color="gray.500">
              {row.original.payer_type}
            </Text>
          )}
        </Box>
      )
    },
    {
      Header: 'Actions',
      Cell: ({ row }) => (
        <Flex gap={2}>
          <Button 
            size="sm" 
            colorScheme="blue"
            onClick={() => window.open(`/ionmform?caseId=${row.original.external_case_id}`, '_blank')}
          >
            View
          </Button>
          <Button 
            size="sm" 
            colorScheme="green"
            onClick={() => window.open(`/analyze-case-mn/${row.original.external_case_id}`, '_blank')}
          >
            Analyze
          </Button>
        </Flex>
      )
    }
  ], []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: cases,
      manualSortBy: true,
      disableMultiSort: true,
    },
    useSortBy
  );

  if (loading && cases.length === 0) {
    return (
      <Box>
        <HeaderMenus />
        <Flex justify="center" align="center" height="200px">
          <Spinner size="xl" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      <HeaderMenus />
      <Box padding={4}>
        {/* Search and Filters */}
        <Flex justifyContent="space-between" mb={4} wrap="wrap" gap={4}>
          <Flex gap={2} align="center">
            <Input
              placeholder="Search cases..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              width="250px"
            />
            <Select
              placeholder="All Case Types"
              value={caseTypeFilter}
              onChange={(e) => setCaseTypeFilter(e.target.value)}
              width="150px"
            >
              <option value="IONM">IONM</option>
              <option value="Surgery">Surgery</option>
            </Select>
            <Select
              placeholder="All Statuses"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              width="150px"
            >
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </Select>
            <Button onClick={handleSearch} colorScheme="blue">
              Search
            </Button>
            <Button 
              onClick={() => {
                setSearchTerm('');
                setCaseTypeFilter('');
                setStatusFilter('');
                setCurrentPage(1);
                fetchCases(1, '', '', '');
              }}
            >
              Clear
            </Button>
          </Flex>

          {/* Pagination */}
          <Flex align="center" gap={2}>
            <Button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              size="sm"
            >
              Previous
            </Button>
            <Text fontSize="sm">
              Page {currentPage} of {totalPages} ({totalCount} total)
            </Text>
            <Button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              size="sm"
            >
              Next
            </Button>
          </Flex>
        </Flex>

        {/* Table */}
        <Box overflowX="auto">
          <Table {...getTableProps()} size="sm">
            <Thead>
              {headerGroups.map(headerGroup => (
                <Tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <Th 
                      {...column.getHeaderProps()} 
                      onClick={() => column.canSort && handleSort(column)}
                      cursor={column.canSort ? "pointer" : "default"}
                    >
                      {column.render('Header')}
                      <span>
                        {sortBy[0]?.id === column.id
                          ? sortBy[0]?.desc
                            ? ' 🔽'
                            : ' 🔼'
                          : ''}
                      </span>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {rows.map(row => {
                prepareRow(row);
                return (
                  <Tr {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <Td {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>

        {loading && (
          <Flex justify="center" mt={4}>
            <Spinner />
          </Flex>
        )}

        {!loading && cases.length === 0 && (
          <Flex justify="center" mt={4}>
            <Text>No cases found. Try adjusting your search criteria.</Text>
          </Flex>
        )}
      </Box>
    </Box>
  );
}

export default ExternalCaseTable;
